import {BASIS, MsgLabel, OSkinableInit} from "back/commons/basis";
import {IReg, IRegPointer, REG} from "lib/commons/registry";
import {DOM, JSX} from "lib/commons/xml/dom";
import {DOMSH} from "lib/commons/xml/domsh";
import {GridSmall, OGridSmallInit} from "back/commons/widgets/grid-tags";
import {GridColDef, ICellBuilder, IGridLineDrawer, IRowSortFn} from "back/commons/widgets/grid-core";
import {CellBuilderDate, CellBuilderEnum, CellBuilderIconLabel, CellBuilderLabel, CellBuilderString, GridDataRowJson} from "back/commons/widgets/grid-libs";
import {BarActions, OBarActionsInit} from "back/commons/widgets/bars";
import {AccelKeyMgr, ACTION, Action, ActionWrapper, IAction} from "lib/commons/actions";
import {GridDataHolderJsonTree, GridDataRowJsonTree} from "back/commons/widgets/tree";
import "back/wsp/dialogs/dialogs_Perms";
import {IContextMenuActionsPointer} from "back/commons/actionables";
import {IChainEnv} from "lib/wsp/chain";
import {EWspLoadingStatus, IWspEnv, IWspPointer, IWspUiEnv, JInfoWsp, JWspInfoInList, Wsp, WSP} from "lib/wsp/wsp";
import {
	CreateDrfWspAction,
	CreateDrvWspAction,
	CreateWspFromWspAction,
	DeletePermanentlyWspsAction,
	DeleteWspAction,
	DeleteWspsAction,
	ICreateWspCtx,
	IWspActionCtx,
	IWspsActionCtx,
	OpenPermsWspAction,
	OpenPropsWspAction,
	OpenPropsWspsAction,
	RestoreWspsAction
} from "back/wsp/actions/wspActions";
import {LANG} from "lib/commons/lang";
import {BaseAreaViewAsync, IView, IViewsContainer, OViewVisitOptions} from "lib/commons/views";
import {IWspsLivePlacePointer} from "lib/wsp/wspsLive";
import {WSPPACK} from "lib/wsp/pack";
import {EUserAspects, JUser, USER} from "lib/core/user";
import {Tabs} from "back/commons/widgets/tabs";
import {AreaAsync} from "lib/commons/areas";
import {CreateWspAction, ImportWspAction} from "back/wsp/actions/chainActions";
import {OpenWsp} from "back/wsp/views/wspList";
import {POPUP} from "back/commons/widgets/popups";
import {AppFrameDeskFeat, IApp} from "back/core/appFrame";
import Reg = REG.Reg;


export interface OWspsMgrInit extends OSkinableInit {
	reg: IReg<IChainEnv>;
	tabAreasLists?: string
}

export class WspsMgr<E extends IRegPointer<IChainEnv>> extends BaseAreaViewAsync<E> implements IViewsContainer {
	reg: IReg<IChainEnv>;
	_tabs: Tabs;
	_msg: MsgLabel;

	protected async _initialize(init: OWspsMgrInit) {
		this.reg = init.reg;
		this._msg = <MsgLabel/> as MsgLabel;
		this.reg.addToList("wsps-mgr-tab", "alive", 1, new AreaAsync<OWspsListMgrInit, WspsListMgr<E>>()
			.setLabel("Ateliers")
			.setBodyBuilder((ctx) => {
				return <AliveWspsMgr î={{reg: this.reg} as OWspsListMgrInit}/> as AliveWspsMgr<E>;
			}), 1);
		if (this.reg.env.universe.config.backEnd === "odb") {
			this.reg.addToList("wsps-mgr-tab", "trashed", 1, new AreaAsync<OWspsListMgrInit, WspsListMgr<E>>()
				.setLabel("Corbeille")
				.setDescription("Ateliers supprimés placés en corbeille")
				.setBodyBuilder((ctx) => {
					return <TrashedWspsMgr î={{reg: this.reg} as OWspsListMgrInit}/> as TrashedWspsMgr<E>;
				}), 1);
		}

		this._tabs = <Tabs areas-context="wsps-mgr-tab-ctxt" areas-list={init.tabAreasLists || "wsps-mgr-tab"}/> as Tabs;

		this._attach(this.tagName, init, this._tabs);
	}

	onViewBeforeHide(close?: boolean): boolean {
		return this._tabs.selectedTab.view.onViewBeforeHide(close);
	}

	async onViewWaitForHide(close?: boolean): Promise<boolean> {
		return this._tabs.selectedTab.view.onViewWaitForHide(close);
	}

	visitViews(visitor: (view: IView) => any, options?: OViewVisitOptions): any {
		if (this._tabs) this._tabs.visitViews(visitor, options);
	}

	visitViewsAsync(visitor: (view: IView) => Promise<any>, options?: OViewVisitOptions): Promise<any> {
		if (this._tabs)
			return this._tabs.visitViewsAsync(visitor, options);
	}

}

REG.reg.registerSkin('wsps-wspsmgr', 1, /* language=CSS */ `
	:host {
		display: flex;
		flex: 1;
		min-width: 0;
		min-height: 0;
	}

	c-tabs {
		flex: 1;
	}
`);

customElements.define("wsps-wspsmgr", WspsMgr);


export interface OWspsListGridInit extends OGridSmallInit {
	/** Actions associées aux items sélectionnés (menu contextuel et IFocusActionables). */
	actions?: IAction<IWspsActionCtx | IWspActionCtx | IRegPointer<IChainEnv>>[]

	/** Racourcis claviers à capter sur la sel courante. */
	accelKeyMgr?: AccelKeyMgr<IWspsActionCtx | IWspActionCtx | IRegPointer<IChainEnv>>

	/** Supprime le lineDrawer par défaut (avec null) ou customise le lineDrawer. */
	lineDrawer?: null | IGridLineDrawer

}

export interface OWspsListMgrInit extends OSkinableInit {
	reg: IReg<IChainEnv>
	/** Init de la liste des ateliers. */
	wspsGrid?: OWspsListGridInit
}

abstract class WspsListMgr<E extends IRegPointer<IChainEnv>> extends BaseAreaViewAsync<E> implements IGridLineDrawer {

	reg: IReg<IChainEnv>;

	protected wspsToolbar: BarActions<IWspsActionCtx | IWspActionCtx | IRegPointer<IChainEnv>>;

	protected wspsActions: IAction<IWspsActionCtx | IWspActionCtx | IRegPointer<IChainEnv>>[];

	protected wspsActionsContext: (IWspsActionCtx | IWspActionCtx | IRegPointer<IChainEnv>) & ICreateWspCtx = {
		openWspAfterCreate: false,
	};

	protected filterInput: HTMLInputElement;

	protected grid: GridSmall & IContextMenuActionsPointer<IWspsActionCtx | IWspActionCtx | IRegPointer<IChainEnv>>;

	protected _lastListWsps: (JWspInfoInList & IWspPointer)[];

	protected dataHolder: GridDataHolderJsonTree<JWspInfoInList & IWspPointer>;

	protected fetchState: 'pending' | 'done' | 'failed';

	protected async _initialize(init: OWspsListMgrInit): Promise<void> {
		this.reg = REG.createSubReg(init.reg);
		this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin("wsps-list-wspsmgr", init);
	}

	protected async initUi(init: OWspsListMgrInit): Promise<void> {
		const sr = this.shadowRoot;

		this.grid = new GridSmall();

		this._onWspLiveStateChange = this.onWspLiveStateChange.bind(this);
		// Pour suivi des ateliers **chargés** uniquement
		(this.reg.env as IWspsLivePlacePointer).place = this.reg.env.universe.wspServer.wspsLive.newPlace();
		(this.reg.env as IWspsLivePlacePointer).place.eventsMgr.on("wspLiveStateChange", this._onWspLiveStateChange);

		/** Init les listes du registre **/
		this.reg.addToList("actions:wsps:wspsmgr:toolbar", "actionRefreshUi", 1, actionRefreshUi);

		/** Accelkey **/
		let accelKeyMgr = new AccelKeyMgr().initFromMapActions(this.reg.mergeListsAsMap("accelkeys:wsps:wspsmgr"));

		/** Toolbar **/
		this.wspsActions = this.reg.mergeLists("actions:wsps:wspsmgr:toolbar", "actions:wsps:wspsmgr");
		accelKeyMgr.initFromMapActions(this.reg.getListAsMap("accelkeys:wspApp:global"));

		let headerElt = sr.appendChild(<div id="header"/>);
		this.filterInput = headerElt.appendChild(<div id="search"/>).appendChild(<input placeholder="Filtrer..." type="search" oninput={this.onFilterInput}/>) as HTMLInputElement;
		this.wspsToolbar = headerElt.appendChild(new BarActions<IWspsActionCtx | IWspActionCtx | IRegPointer<IChainEnv>>().initialize({
			reg: this.reg,
			actions: ACTION.injectSepByGroup(this.wspsActions, this.reg.getPref("actions.wsps.wspsmgr.groupOrder", "wsps * refresh"), this.wspsActionsContext),
			uiContext: "bar",
			actionContext: this.wspsActionsContext,
			disableFullOverlay: true,
		} as OBarActionsInit<IWspsActionCtx | IWspActionCtx | IRegPointer<IChainEnv>>));

		/** Grid **/
		this.dataHolder = new GridDataHolderJsonTree<JWspInfoInList>('wsps');

		const initPacksGrid: OWspsListGridInit = BASIS.newInit(init.wspsGrid, this.reg);
		if (initPacksGrid.skinScroll === undefined) initPacksGrid.skinScroll = 'scroll/small';
		if (initPacksGrid.lineDrawer === undefined) initPacksGrid.lineDrawer = this;
		if (initPacksGrid.accelKeyMgr === undefined) initPacksGrid.accelKeyMgr = accelKeyMgr;
		initPacksGrid.dataHolder = this.dataHolder;
		initPacksGrid.selType = "multi";
		initPacksGrid.emptyBody = this.emptyBody.bind(this);
		initPacksGrid.skinOver = "wspsmgr-list-grid";
		initPacksGrid.autoSelOnFocus = 'first';
		initPacksGrid.defaultAction = OpenPropsWspAction.SINGLETON;

		this.grid.initialize(initPacksGrid);

		this.grid.ctxMenuActions = {
			actions: ACTION.injectSepByGroup(this.reg.mergeLists("actions:wsps:wspsmgr:ctxtmenu", "actions:wsps:wspsmgr") as IAction<IWspsActionCtx | IWspActionCtx | IRegPointer<IChainEnv>>[], this.reg.getPref("actions.wsps.wspsmgr.groupOrder", "wsps wps * refresh"), this) as IAction<IWspsActionCtx | IWspActionCtx | IRegPointer<IChainEnv>>[],
			actionContext: this.wspsActionsContext,
		};

		this.grid.addEventListener('keydown', (ev: KeyboardEvent) => {
			accelKeyMgr.handleKeyboardEvent(ev, this);
		});

		this.grid.addEventListener('grid-select', function (this: GridSmall, ev: Event) {
			const wspsMgr = DOMSH.findHost<WspsListMgr<any>>(this);
			// Refresh de l'actionContext des actions
			let selectedWsps = wspsMgr.getSelectedWsps();
			(wspsMgr.wspsActionsContext as IWspActionCtx).reg = (wspsMgr.wspsActionsContext as IWspsActionCtx).regs = undefined;
			if (!selectedWsps || selectedWsps.length == 0) {
				(wspsMgr.wspsActionsContext as IWspActionCtx).reg = wspsMgr.reg;
			} else if (selectedWsps.length == 1) {
				(wspsMgr.wspsActionsContext as IWspActionCtx).reg = selectedWsps[0].reg;
			} else {
				(wspsMgr.wspsActionsContext as IWspActionCtx).reg = wspsMgr.reg;
				(wspsMgr.wspsActionsContext as IWspsActionCtx).regs = [];
				selectedWsps.forEach((wsp) => {
					(wspsMgr.wspsActionsContext as IWspsActionCtx).regs.push(wsp.reg);
				});
			}
			wspsMgr.wspsToolbar.refreshContent();
		});

		this.grid.uiEvents.on('rowDblclick', (row: GridDataRowJson<JWspInfoInList> | null, ev: MouseEvent) => {
			OpenPropsWspAction.SINGLETON.executeIfAvailable((this.wspsActionsContext as IWspActionCtx), ev);
		});

		this.grid.setAttribute("c-resizable", "");
		sr.appendChild(this.grid);

		this.dataHolder.connectToGrid(this.grid);

		return this.refresh();
	}

	/**
	 * Refresh UI
	 * Gestion de la réentrance
	 */
	async _refresh() {
		this.refreshFreeze(1);
		try {
			super._refresh();
			const selectedWsps = this.getSelectedWsps();
			this.filterInput.value = "";
			this.fetchState = "pending";
			this._lastListWsps = await this.listWsps();
			this._lastListWsps.sort((w1, w2) => (w1.title || w1.wspCd).localeCompare(w2.title || w2.wspCd));
			this.fetchState = "done";
			// On ne suit QUE les ateliers loadés (besoin : maj des colonnes qui affichent le statut)
			this._lastListWsps.forEach((wspInfos) => {
				if (wspInfos.status !== EWspLoadingStatus.notLoaded && !(this.reg.env as IWspsLivePlacePointer).place.isConcernedByWsp(wspInfos.wspCd))
					(this.reg.env as IWspsLivePlacePointer).place.registerWsp(this.getRowDatasWsp(wspInfos));
			});

			this.dataHolder.setDatas(this._lastListWsps);
			if (selectedWsps)
				this.selectByWsp(selectedWsps);
			else
				this.grid.setSelectedRows(0);
			this.grid.focus();
		} catch (e) {
			this.fetchState = "failed";
			this._lastListWsps = [];
			this.dataHolder.setDatas([]);
			throw e;
		} finally {
			this.refreshFreeze(-1);
		}
	}

	/** Retourne l'objet WSP associé à une row */
	protected getRowDatasWsp(rowData: JWspInfoInList & IWspPointer) {
		if (!rowData.wsp)
			rowData.wsp = new Wsp(this.reg.env.universe.wspServer, rowData.wspCd, rowData as JInfoWsp);
		return rowData.wsp;
	}

	/** Retourne la liste des ateliers à afficher */
	abstract listWsps(): Promise<(JWspInfoInList & IWspPointer)[]>

	/**
	 * Tree : affichage explicite des lignes en erreur
	 * @param row
	 * @param line
	 */
	redrawLine(row: GridDataRowJson<JWspInfoInList & IWspPointer>, line: HTMLElement): void {
		DOM.setAttrBool(line, "data-removed", row.rowDatas.status==EWspLoadingStatus.noWsp);
	}

	/**
	 * Tree : fonction retournant le texte à afficher en cas de tree vide
	 */
	protected emptyBody() {
		switch (this.fetchState) {
		case "done":
			return <c-msg>Aucun atelier</c-msg>;
		case "failed":
			return <c-msg level="error">Échec au chargement</c-msg>;
		default:
			return <c-msg>Chargement en cours...</c-msg>;
		}
	}

	/**
	 * Filter
	 * @param ev
	 */
	protected onFilterInput(this: HTMLInputElement, ev: Event) {
		const me = DOMSH.findHost<WspsListMgr<any>>(this);
		const selectedWsps = me.getSelectedWsps();
		const text = this.value;
		if (text) {
			const pattern = new RegExp(LANG.escape4RegexpFuzzy(text), 'i');
			const fullArr = me.dataHolder.getDatas();
			const arr = [] as JWspInfoInList[];
			for (let i = 0, s = fullArr.length; i < s; i++) {
				const v = fullArr[i];
				if (pattern.test(v.title)) arr.push(v);
			}
			me.dataHolder.setDatas(arr);
		} else {
			me.dataHolder.setDatas(me._lastListWsps);
		}
		if (selectedWsps)
			me.selectByWsp(selectedWsps);
	}

	/**
	 * Sélection des entrées de l'arbre via une liste de JWspInfoInList sur un tree **chargé**
	 * Si un itemType demandé n'est pas présent dans le tree, aucune erreur n'est remontée
	 * @param JWspInfoInList[]
	 */
	selectByWsp(wsps: Wsp[]): void {
		if (wsps != null) {
			const selectedRows: number[] = [];
			for (let i = 0, c = this.dataHolder.countRows(); i < c; i++) {
				const wspRow = this.dataHolder.getRow(i).rowDatas as JWspInfoInList;
				if (wspRow && wsps.find(wsp => {
					if (wsp && wsp.code == wspRow.wspCd)
						return true;
				})) {
					selectedRows.push(i);
				}
			}
			this.grid.setSelectedRows(selectedRows);
		}
	}

	getSelectedWsps(): Wsp[] {
		const wsps: Wsp[] = [];
		if (this.dataHolder) {
			this.dataHolder.getSelectedDatas().forEach((entry) => {
				wsps.push(this.getRowDatasWsp(entry));
			});
		}
		return wsps;
	}

	onViewShown() {
		if (this.initialized) this.refresh();
	}

	onViewBeforeHide(close?: boolean): boolean {
		return true;
	}

	onViewHidden(closed?: boolean): void {
		if (this.initialized) {
			if (closed)
				(this.reg.env as IWspsLivePlacePointer).place.closePlace();
		} else {
			this.initializedAsync.then(() => this.onViewHidden());
		}
	}

	protected _onWspLiveStateChange: () => void;

	async onWspLiveStateChange(wsp: Wsp) {
		let datas = this.dataHolder.getDatas();
		let wspEntry = datas.find((entry) => entry.wspCd === wsp.code);
		if (wspEntry) {
			const offset = this.dataHolder.getOffset(wspEntry);
			let infoWsp = wsp.infoWsp || wsp.infoWspError;
			if (wsp.isDeleted) {
				this.dataHolder.updateDatasInTree(null, offset, 1);
			} else if (infoWsp) {
				Object.assign(wspEntry, infoWsp);
				this.grid.invalidateRows(offset, 1);
			}
		}
	}


}

REG.reg.registerSkin('wsps-list-wspsmgr', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}

	#header {
		display: flex;
		/*flex-direction: column;*/
	}

	c-bar-shared {
		flex: unset;
		min-height: min-content;
		min-width: min-content;
	}

	input {
		flex: 1;
	  padding: 2px;
	  background: none;
	  color: var(--form-color);
	  border: none;
	  font-size: inherit;
  }

  #search {
	  display: flex;
	  flex: 1;
	  min-height: min-content;
	  min-width: min-content;
	  background: .1em / 1em no-repeat url(/@skin@/commons/icons/filter.svg) var(--form-search-bgcolor);
	  padding-inline-start: 1.2em;
	  border-inline-end: 1px solid var(--border-color);
  }

  input:focus {
	  outline: none;
  }

  input::placeholder {
	  color: var(--fade-color);
	  letter-spacing: 2px;
	  font-size: .8em;
	  font-style: italic;
  }

  input:focus::placeholder {
	  color: transparent;
  }
`);

REG.reg.registerSkin('wspsmgr-list-grid', 1, /* language=CSS */ `
	:host {
		flex: 1;
	}

	.warning {
		font-style: italic;
		color: var(--warning-color);
  }

  .error {
	  color: var(--error-color);
  }

  .trashed {
	  text-decoration-line: line-through;
	  font-style: italic;
  }
	.line[data-removed]{
	  text-decoration:line-through;
	}

`);


/** Grid des ateliers lives */
export class AliveWspsMgr<E extends IRegPointer<IChainEnv>> extends WspsListMgr<E> {
	protected async _initialize(init: OWspsListMgrInit): Promise<void> {
		await super._initialize(init);
		this._initAndInstallSkin("wsps-alive-wspsmgr", init);

		/** Init les listes du registre **/
		/** Actions : contexte menu ET toolbar **/
		this.reg.addToList("actions:wsps:wspsmgr", "actionCreateWsp", 1, new ActionWrapperRefreshWspMgr(this).setOverridenSvc(CreateWspAction.SINGLETON), 1);
		this.reg.addToList("actions:wsps:wspsmgr", "actionCreateWspFromWsp", 1, new ActionWrapperRefreshWspMgr(this).setOverridenSvc(CreateWspFromWspAction.SINGLETON), 1);
		this.reg.addToList("actions:wsps:wspsmgr", "actionDeleteWsp", 1, new ActionWrapperOnWspsChanges(this).setOverridenSvc(new ActionWrapper()
			.setExecute(async function (this: ActionWrapper<any>, ctx: IWspActionCtx, ev?) {
				// Fermeture de l'atelier éventuellement ouvert dans le desk
				const mainApp = (desk as AppFrameDeskFeat).getMainApp();
				if ((mainApp as IApp<IWspEnv>)?.reg?.env.wsp?.equals(ctx.reg.env.wsp))
					if (AppFrameDeskFeat.isIn(desk)) await desk.gotoHome();
				return this.getWrapped(ctx).execute(ctx, ev);
			})
			.setOverridenSvc(DeleteWspAction.SINGLETON)
		));
		this.reg.addToList("actions:wsps:wspsmgr", "actionDeleteWsps", 1, new ActionWrapperOnWspsChanges(this).setOverridenSvc(new ActionWrapper()
			.setExecute(async function (this: ActionWrapper<any>, ctx: IWspsActionCtx, ev?) {
				// Fermeture de l'atelier éventuellement ouvert dans le desk
				const mainApp = (desk as AppFrameDeskFeat).getMainApp();
				if (mainApp && ctx.regs?.length) {
					for (const reg of ctx.regs) {
						if ((mainApp as IApp<IWspEnv>)?.reg?.env.wsp.equals(reg.env.wsp)) {
							if (AppFrameDeskFeat.isIn(desk)) await desk.gotoHome();
							break;
						}
					}
				}
				return this.getWrapped(ctx).execute(ctx, ev);
			})
			.setOverridenSvc(DeleteWspsAction.SINGLETON)
		));
		this.reg.addToList("actions:wsps:wspsmgr", "actionOpenPropsWsp", 1, new ActionWrapperOnWspsChanges(this).setOverridenSvc(OpenPropsWspAction.SINGLETON));
		this.reg.addToList("actions:wsps:wspsmgr", "actionOpenPermsWsp", 1, new ActionWrapperOnWspsChanges(this).setOverridenSvc(OpenPermsWspAction.SINGLETON));
		this.reg.addToList("actions:wsps:wspsmgr", "actionPropsWspsModel", 1, new ActionWrapperOnWspsChanges(this).setOverridenSvc(OpenPropsWspsAction.SINGLETON));

		/** Actions : toolbar
		 * 		liste actions:wsps:wspsmgr:toolbar uniquement
		 */
		this.reg.addToList("actions:wsps:wspsmgr:toolbar", "actionRefreshUi", 1, actionRefreshUi);
		this.reg.addToList("actions:wsps:wspsmgr:toolbar", "actionImportWsp", 1, new ActionWrapperRefreshWspMgr(this).setOverridenSvc(ImportWspAction.SINGLETON), 2);

		/** Actions : ctxtmenu
		 * 		liste actions:wsps:wspsmgr:ctxtmenu uniquement
		 */
		const wspsMgr = this;
		this.reg.addToList("actions:wsps:wspsmgr:ctxtmenu", "actionCreateDrfWsp", 1, new ActionWrapperRefreshWspMgr(this).setOverridenSvc(CreateDrfWspAction.SINGLETON));
		this.reg.addToList("actions:wsps:wspsmgr:ctxtmenu", "actionCreateDrvWsp", 1, new ActionWrapperRefreshWspMgr(this).setOverridenSvc(CreateDrvWspAction.SINGLETON));
		this.reg.addToList("actions:wsps:wspsmgr:ctxtmenu", "actionOpenWsp", 1, new ActionWrapper()
			.setExecute(function (this: ActionWrapper<any>, ctx: IWspActionCtx, ev?) {
				const popup = POPUP.findPopupableParent(wspsMgr);
				if (popup) popup.close();
				return this.getWrapped(ctx).execute(ctx, ev);
			})
			.setOverridenSvc(new OpenWsp(WSP.defaultWspOpenMode(this.reg)).setLabel("Ouvrir l'atelier..."))
		);

		let columnDefs = [];
		columnDefs.push(
			new GridColDef('title').setLabel("Titre")
				.setDefaultSort(1, 'ascendant')
				.setFlex('25em', 1, 1).setMinWidth('8em').setSortable(true)
				.setCellBuilder(new CellBuilderString("title").override("_getValue", (row: GridDataRowJson<JWspInfoInList>): any => {
					if (row.rowDatas.title) return row.rowDatas.title;
					let wsp = this.getRowDatasWsp(row.rowDatas);
					return wsp.wspTitle || "[Non titré]";
				})),
			new GridColDef('wspCd').setLabel("Modèle")
				.setFlex('15em', 1, 1).setMinWidth('6em').setSortable(true)
				.setCellBuilder(new CellBuilderWspModel("wspType"))
		);
		if (this.reg.env.universe.adminUsers?.hasAspect(EUserAspects.rolable)) {
			columnDefs.push(new GridColDef('wspSetPerms').setLabel("Permissions affectées").setDescription("Permissions affectées sur l'atelier")
				.setFlex('10em', 1, 1).setMinWidth('6em').setSortable(true)
				.setCellBuilder(new CellBuilderWspSpecifiedRoles()));
		}
		columnDefs.push(new GridColDef('status').setLabel("Statut")
			.setFlex('5em', 1, 1).setMinWidth('4em').setSortable(true)
			.setCellBuilder(new CellBuilderEnum('status', new Map([[EWspLoadingStatus.notLoaded, ""], [EWspLoadingStatus.loading, "Chargement en cours..."], [EWspLoadingStatus.noWsp, "Supprimé"], [EWspLoadingStatus.ok, "Opérationnel"], [EWspLoadingStatus.failed, "Non accessible"]]))
				.setCellClass("center")
				.setDescriptionFunc((row: GridDataRowJson<JWspInfoInList>) => {
					const key = row.getData("status") || "";
					return key === EWspLoadingStatus.notLoaded ? "Information non disponible" : "";
				})));

		if (!init.wspsGrid) init.wspsGrid = [] as OWspsListGridInit;
		if (init.wspsGrid.columnDefs === undefined) init.wspsGrid.columnDefs = columnDefs;

		return this.initUi(init);
	}

	async listWsps(): Promise<JWspInfoInList[]> {
		return (await WSP.listWsps(this.reg.env.universe.wspServer, {fields: ["srcRoles", "srcRi"], withWspSrcSpecifiedRoles: true})).wsps;
	}

}

customElements.define("wsps-alive-wspsmgr", AliveWspsMgr);
REG.reg.registerSkin('wsps-alive-wspsmgr', 1, /* language=CSS */ `

`);

/** Grid des ateliers en corbeille */
export class TrashedWspsMgr<E extends IRegPointer<IChainEnv>> extends WspsListMgr<E> {
	protected async _initialize(init: OWspsListMgrInit): Promise<void> {
		await super._initialize(init);
		this._initAndInstallSkin("wsps-trashed-wspsmgr", init);

		/** Init les listes du registre **/
		this.reg.addToList("actions:wsps:wspsmgr", "actionDeletePermanentlyWsps", 1, new ActionWrapperRefreshWspMgr(this).setOverridenSvc(DeletePermanentlyWspsAction.SINGLETON));
		this.reg.addToList("actions:wsps:wspsmgr", "actionRestoreWsps", 1, new ActionWrapperRefreshWspMgr(this).setOverridenSvc(RestoreWspsAction.SINGLETON));

		let columnDefs = [];
		columnDefs.push(
			new GridColDef('title').setLabel("Titre")
				.setDefaultSort(1, 'ascendant')
				.setFlex('25em', 1, 1).setMinWidth('8em').setSortable(true)
				.setCellBuilder(new CellBuilderIconLabel("title").override("_getValue", (row: GridDataRowJson<JWspInfoInList>): any => {
					if (row.rowDatas.title) return row.rowDatas.title;
					let wsp = this.getRowDatasWsp(row.rowDatas);
					return wsp.wspTitle || "[Non titré]";
				}).override("_getIcon", (row: GridDataRowJsonTree<JUser>) => {
					return "/@skin@/wsp/objects/wsp/wspTrashed.svg";
				})),
			new GridColDef('dt')
				.setLabel("Date de suppression").setSortable(true)
				.setFlex('6em', 1, 1).setMinWidth('2em').setSortable(true)
				.setCellBuilder(new CellBuilderDate('srcDt').setCellClass("center").override("_getValue", (row: GridDataRowJson<JWspInfoInList>): any => (row.rowDatas.srcFields as any).srcDt))
		)

		if (!init.wspsGrid) init.wspsGrid = [] as OWspsListGridInit;
		if (init.wspsGrid.columnDefs === undefined) init.wspsGrid.columnDefs = columnDefs;

		return this.initUi(init);
	}

	async listWsps(): Promise<JWspInfoInList[]> {
		return (await WSP.listWsps(this.reg.env.universe.wspServer, {inTrash: true, fields: ["srcDt", "srcRoles", "srcRi"]})).wsps;
	}

	redrawLine(row: GridDataRowJson<JWspInfoInList>, line: HTMLElement): void {
		DOM.addClass(line, "trashed");
	}

	protected emptyBody() {
		switch (this.fetchState) {
		case "done":
			return <c-msg>Aucun atelier en corbeille</c-msg>;
		default:
			return super.emptyBody();
		}
	}
}

customElements.define("wsps-trashed-wspsmgr", TrashedWspsMgr);
REG.reg.registerSkin('wsps-trashed-wspsmgr', 1, /* language=CSS */ `

`);

/** Actions **/

/**
 * Refresh UI
 */
const actionRefreshUi = new Action<any>("refreshUi")
	.setLabel("Rafraichir")
	.setGroup("refresh")
	.setIcon("/@skin@/commons/icons/refresh.svg")
	.setExecute(async function (this: Action<any>, ctx: any, ev: Event) {
			const me = DOMSH.findHost<WspsListMgr<any>>(ev.target as Node);
			await me.refresh();
		}
	);


/** CellBuilder **/

/**  Colonne de titre du wsp pack */
class CellBuilderWspModel extends CellBuilderLabel {

	redrawCell(row: GridDataRowJson<JWspInfoInList>, root: HTMLElement): void {
		super.redrawCell(row, root);
		if (row.rowDatas.wspTypeWarn) {
			DOM.addClass(root, "warning");
		} else
			DOM.removeClass(root, "warning");
	}

	protected _getValue(row: GridDataRowJson<JWspInfoInList>): any {
		return WSPPACK.buildWspTypeTitle(row.rowDatas, {ext: null, version: true, lang: true}) || (row.rowDatas.wspTypeWarn === 'unknown' ? "Inconnu" : "");
	}

	protected _getDescription(row: GridDataRowJson<JWspInfoInList>): any {
		let desc = [];
		if (row.rowDatas.wspTypeWarn === 'unknown') desc.push("Modèle documentaire inconnu");
		desc.push(WSPPACK.buildWspTypeTitle(row.rowDatas, {ext: true, version: true, lang: true}));
		return desc.join("\n");
	}
}

/**  Colonne des permissions locales */
class CellBuilderWspSpecifiedRoles implements ICellBuilder {

	redrawCell(row: GridDataRowJson<JWspInfoInList>, root: HTMLElement) {
		const reg: Reg<IWspUiEnv> = REG.findReg(root) as Reg<IWspUiEnv>;
		DOM.addClass(root, "center");
		// FIXME : contournement du fait que la row peut évoluer.
		const cacheHolder = row.cacheHolder;
		let cache: string | Promise<void> = cacheHolder["specifRolesBody"];
		if (cache == null) {
			//1èr redraw pour cette row.
			let accounts: string[];
			if (row.rowDatas.srcSpecifiedRoles && (accounts = Object.keys(row.rowDatas.srcSpecifiedRoles)).length > 0) {
				// Affichage des users en async
				DOM.setTextContent(root, accounts.length.toString());
				const useUsers = reg.env.universe.useUsers;
				const userAccounts = Object.keys(row.rowDatas.srcSpecifiedRoles);
				cacheHolder["specifRolesBody"] = cache = Promise.all(userAccounts.map((account) => useUsers.getUserBatch(account))).then((users: JUser[]) => {
					if (cacheHolder["specifRolesBody"] !== cache) return; //raceCond : les données de cette row ont évoluées.
					const nickNames: string[] = [];
					for (let i = 0; i < userAccounts.length; i++) {
						const user = users[i];
						nickNames.push(user ? USER.getPrimaryName(user) : userAccounts[i]);
					}
					nickNames.sort((a, b) => a.toLocaleLowerCase().localeCompare(b.toLocaleLowerCase()));
					cacheHolder["specifRolesTitle"] = nickNames.join("\n");
					const count = nickNames.length;
					if (count > 5) {
						cacheHolder["specifRolesBody"] = count.toString();
					} else {
						const names = nickNames.join(", ");
						cacheHolder["specifRolesBody"] = `${count} (${names})`;
					}
				});
			} else {
				//Pas de SpecifiedRoles
				cache = cacheHolder["specifRolesBody"] = "-";
			}
		}
		if (cache instanceof Promise) {
			//cache non reseté mais calcul en cours, on attend, on réaffiche le nombre en attendant.
			DOM.setTextContent(root, Object.keys(row.rowDatas.srcSpecifiedRoles).length.toString());
			(root as any).specifRolesPending = cache; //raceCond pour refresh async de la line HTML.
			cache.then(() => {
				if ((root as any).specifRolesPending !== cache) return; //raceCond : cette line HTML a été redessinée
				(root as any).specifRolesPending = null; //cleanup
				DOM.setTextContent(root, cacheHolder["specifRolesBody"]);
				DOM.setAttr(root, "title", cacheHolder["specifRolesTitle"]);
			});
		} else {
			(root as any).specifRolesPending = null; //raceCond pour refresh async de la line HTML.
			DOM.setTextContent(root, cache);
			DOM.setAttr(root, "title", cache === "-" ? null : cacheHolder["specifRolesTitle"]);
		}
	}

	protected _getValue(row: GridDataRowJson<JWspInfoInList>): any {
		return WSPPACK.buildWspTypeTitle(row.rowDatas, {ext: null, version: true, lang: true}) || (row.rowDatas.wspTypeWarn === 'unknown' ? "Inconnu" : "");
	}

	getColSortFn(): IRowSortFn | null {
		return undefined;
	}

}

/**
 * Wrapper d'une action qui déclenche un refresh du tree
 */
class ActionWrapperRefreshWspMgr<C> extends ActionWrapper<C> {
	constructor(public wspsMgr: WspsListMgr<any>) {
		super();
	}

	async execute(ctx: C, ev?: Event): Promise<any> {
		let result = await super.execute(ctx, ev);
		await this.wspsMgr._refresh();
		if (typeof result === "object") {
			let wsp = result as Wsp;
			this.wspsMgr.selectByWsp([wsp]);
		}
		return result;
	}
}


/**
 * Wrapper d'une action qui déclenche le suivi des modifs de ce(s) ateliers, et refresh UI de ces ateliers
 */
class ActionWrapperOnWspsChanges<C extends IWspActionCtx & IWspsActionCtx> extends ActionWrapper<C> {
	constructor(public wspsMgr: WspsListMgr<any>) {
		super();
	}

	async execute(ctx: C, ev?: Event): Promise<any> {
		if (ctx.regs) {
			ctx.regs.forEach((entry) => {
				if (entry.env.wsp && !(this.wspsMgr.reg.env as IWspsLivePlacePointer).place.getWsp(entry.env.wsp.code)) (this.wspsMgr.reg.env as IWspsLivePlacePointer).place.registerWsp(entry.env.wsp);
			});
		} else if (ctx.reg?.env.wsp && !(this.wspsMgr.reg.env as IWspsLivePlacePointer).place.getWsp(ctx.reg.env.wsp.code))
			(this.wspsMgr.reg.env as IWspsLivePlacePointer).place.registerWsp(ctx.reg.env.wsp);

		await super.execute(ctx, ev);
		/*if(ctx.regs){
			ctx.regs.forEach((entry)=>{
				if(entry.env.wsp)
					entry.env.wsp.forceReload().then((wsp)=>{
						this.wspsMgr.onWspLiveStateChange(wsp);
					});
			});
		}else if(ctx.reg && ctx.reg.env.wsp)
			ctx.reg.env.wsp.forceReload().then((wsp)=>{
				this.wspsMgr.onWspLiveStateChange(wsp);
			});
		*/


	}
}